import {html, TemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {AbstractO2dElement} from "@o2d/shared";
import type {CalculationContext, ColumnDefinitions, RowDefinitions} from "../types/grid-types";
import type {Section} from "../types/section.interface";
import {ScrollController} from "../controllers/scroll.controller";
import {type LayoutChangedDetail, ResizeController} from "../controllers/resize.controller";
import {calculateGridLayoutCssVariables, CssVariablesCalculationResult} from "./calculate-grid-layout-css-variables.fn";
import {StartEndLines} from "../types/start-end-lines";
import type {LayoutTemplate} from "../types/layout-template.interface";

/**
 * Event detail for layout-size-changed event.
 * Contains dimension information and optionally updated grid template data.
 */
export interface LayoutSizeChangedDetail {
    /**
     * Current container and grid dimensions
     */
    dimensions: LayoutChangedDetail;

    /**
     * Updated section start/end lines (only present when cell count changed)
     */
    sectionsStartEndLines?: Map<string, StartEndLines>;

    /**
     * Auto-sized cell counts for both axes (only present when cell count changed)
     */
    countOfAutoAxisCells?: { x: number; y: number };
}

/**
 * O2D Layout Element
 * A performant CSS Grid-based layout component for displaying positioned cells
 */
@customElement("o2d-layout")
export class O2dLayoutElement extends AbstractO2dElement {

    @property()
    public rowsCount: number = 0;

    @property({type: Object})
    layoutTemplate!: LayoutTemplate

    /**
     * Column definitions as a record of functions
     * Each function receives its own key and all column definitions
     */
    @property({type: Object, attribute: false})
    public columnDefinitions: ColumnDefinitions = {};

    /**
     * Row definitions as a record of functions
     * Each function receives its own key and all row definitions
     */
    @property({type: Object, attribute: false})
    public rowDefinitions: RowDefinitions = {};

    @state()
    public dimensions: LayoutChangedDetail = {
        containerWidth: this.getBoundingClientRect().width,
        containerHeight: this.getBoundingClientRect().height,
        columnsCount: 0,
        rowsCount: 0
    };

    /**
     * Scroll remainder values (subpixel positioning)
     */
    @state()
    public remainderX: number = 0;

    @state()
    public remainderY: number = 0;

    /**
     * MutationObserver for watching CSS variable changes
     */
    private styleObserver: MutationObserver | null = null;

    /**
     * Tracked CSS variable values for change detection
     */
    private trackedCssVariables: Map<string, string> = new Map();

    /**
     * CSS variables to monitor for changes
     */
    private cssVariablesToWatch: Set<string> = new Set();

    /**
     * Animation frame ID for polling CSS variables
     */
    private cssVariablePollingFrame: number | null = null;

    /**
     * Flag to control polling
     */
    private isPollingActive: boolean = false;

    /**
     * Next allowed CSS check time in milliseconds
     */
    private nextCssCheckTime: number = 0;

    /**
     * Interval between CSS variable checks in milliseconds
     */
    private readonly CSS_CHECK_INTERVAL: number = 1000;

    /**
     * Count of auto-sized cells on X-axis (T1.7)
     * NOT decorated with @state() to avoid unnecessary re-renders
     */
    private countOfAutoAxisCellsX: number = 0;

    /**
     * Count of auto-sized cells on Y-axis (T1.7)
     * NOT decorated with @state() to avoid unnecessary re-renders
     */
    private countOfAutoAxisCellsY: number = 0;

    /**
     * ScrollController for handling touch, pointer and wheel scrolling
     */
    public scrollController: ScrollController | null = null;

    /**
     * ResizeController for handling container size changes
     */
    public resizeController: ResizeController | null = null;

    /**
     * ResizeObserver bound to a hidden sentinel element whose width is sized
     * by the auto-axis track size variable (e.g. `var(--t3-column-section-3-size)`).
     *
     * Why: when an inherited CSS variable like `--cell-width-multiplication`
     * changes on an ANCESTOR (Lit view-host), the resolved value of the
     * auto-axis track size variable (defined as `calc(40px * var(--cell-width-multiplication))`)
     * changes on this element via the cascade. MutationObservers do NOT fire
     * for cascade-driven recomputations, and polling is rate-limited (~1s).
     * The sentinel's ResizeObserver fires deterministically as soon as the
     * resolved track size changes, regardless of HOW it changed.
     */
    public sentinelResizeObserver: ResizeObserver | null = null;

    /**
     * CSS variable currently bound to the sentinel's `width`.
     * Tracked so we can re-render the sentinel if the layout template changes.
     */
    private sentinelTrackedVariable: string | null = null;

    /*
    protected override willUpdate(changedProperties: PropertyValues): void {
        if (
            changedProperties.has("columnDefinitions")
            ||
            (changedProperties.has("dimensions") && this.dimensions?.containerWidth != changedProperties.get("dimensions")?.containerWidth)
        ) {
            this.calculateColumns();
        }

        if (
            changedProperties.has("rowDefinitions")
            ||
            (changedProperties.has("dimensions") && this.dimensions?.containerHeight != changedProperties.get("dimensions")?.containerHeight)
        ) {
            this.calculateRows();
        }
    }
    */

    /**
     * Recalculates the grid template for both X and Y axes based on current container dimensions.
     *
     * **INTERNAL USE ONLY** - Public for testing purposes only, not part of the public API.
     *
     * This method is called from:
     * - `connectedCallback()` for initial grid setup
     * - `dispatchGridChangedEvent()` when container resizes
     *
     * **Side Effects:**
     * - Modifies CSS properties via `buildCssTemplateAxis()`:
     *   - `grid-template-columns` and `grid-template-rows`
     *   - CSS remainder variables (`--x-remainder`, `--y-remainder`)
     * - Updates internal properties:
     *   - `countOfAutoAxisCellsX` and `countOfAutoAxisCellsY`
     *   - `cssVariablesToWatch`
     *
     * **Idempotency:**
     * X-axis and Y-axis calculations are independent. `getBoundingClientRect()` dimensions
     * are not affected by `grid-template-*` CSS properties (when using flex layout),
     * so calling this method multiple times with the same container size produces
     * identical results.
     *
     * **Return Value:**
     * Returns an object containing:
     * - `sectionsStartEndLines`: Map of section IDs to their grid line positions
     * - `countOfAutoAxisCells`: Number of auto-sized cells per axis `{x: number, y: number}`
     *
     * Returns `null` if `layoutTemplate` is not set (guard condition).
     *
     * @returns Grid layout calculation result, or null if layoutTemplate not set
     * @internal
     */
    public recalculateGridTemplate(): {
        sectionsStartEndLines: Map<string, StartEndLines>;
        countOfAutoAxisCells: { x: number; y: number };
        columnsCount: number;
        rowsCount: number;
    } | null {
        // T1.3: Guard against missing layoutTemplate
        if (!this.layoutTemplate) {
            return null;
        }

        // Determine the auto-axis size variable for X to bind the sentinel against.
        // This is the variable whose resolved value drives `countOfAutoAxisCellsX`,
        // so a cascade-driven change to it MUST trigger a recompute.
        this.updateSentinelTrackedVariable();

        const allWatchVariables: string[] = [];
        let sectionsStartEndLines = new Map<string, StartEndLines>();
        let columnsCount = 0;
        let rowsCount = 0;

        // Build CSS template for both axes
        for (const axis of ['x', 'y']) {
            const {watchVariables, startEndLines, countOfAutoAxisCells, totalTrackCount} = this.buildCssTemplateAxis(
                this.layoutTemplate.name,
                axis as 'x' | 'y'
            );
            allWatchVariables.push(...watchVariables);
            sectionsStartEndLines = new Map([...sectionsStartEndLines.entries(), ...startEndLines.entries()]);

            // Store countOfAutoAxisCells for later comparison (T2)
            if (axis === 'x') {
                this.countOfAutoAxisCellsX = countOfAutoAxisCells;
                columnsCount = totalTrackCount;
            } else {
                this.countOfAutoAxisCellsY = countOfAutoAxisCells;
                rowsCount = totalTrackCount;
            }
        }

        this.cssVariablesToWatch = new Set(allWatchVariables);

        return {
            sectionsStartEndLines,
            countOfAutoAxisCells: {
                x: this.countOfAutoAxisCellsX,
                y: this.countOfAutoAxisCellsY,
            },
            columnsCount,
            rowsCount,
        };
    }

    /**
     * Lifecycle callback when element is connected to DOM
     */
    public override connectedCallback(): void {
        super.connectedCallback();

        // T1.8: Refactor - Call recalculateGridTemplate() instead of inline calculation
        const result = this.recalculateGridTemplate();

        // Initialize Controllers
        this.scrollController = new ScrollController(this);
        this.resizeController = new ResizeController(this);

        // Initialize CSS variable watching
        this.registerCssVariableForWatchingValueChange();

        // Start polling as a fallback for DevTools changes
        this.startCssVariablePolling();

        // Fire connected event with timestamp
        if (result) {
            this.fireEvent("layout-connected", {sectionsStartEndLines: result.sectionsStartEndLines});
        }
    }

    /**
     * Lifecycle callback when element is disconnected from DOM
     */
    public override disconnectedCallback(): void {

        // Clean up Controllers
        if (this.scrollController) {
            this.scrollController.hostDisconnected();
            this.scrollController = null;
        }
        if (this.resizeController) {
            this.resizeController.hostDisconnected();
            this.resizeController = null;
        }

        // Clean up MutationObserver and polling
        this.cleanupCssVariableWatching();
        this.stopCssVariablePolling();

        // Clean up sentinel ResizeObserver
        if (this.sentinelResizeObserver) {
            this.sentinelResizeObserver.disconnect();
            this.sentinelResizeObserver = null;
        }

        // Fire disconnecting event before cleanup
        this.fireEvent("grid-disconnecting", {
            timestamp: Date.now(),
            element: this.tagName.toLowerCase()
        });

        // Call parent disconnectedCallback for cleanup
        super.disconnectedCallback();
    }

    /**
     * Renders the grid layout component.
     *
     * Includes a hidden sentinel `<span>` whose `width` is bound to the
     * auto-axis track size variable. A ResizeObserver on this sentinel
     * detects changes to the resolved track size that come in via CSS
     * cascade (e.g. an ancestor toggling an inherited CSS variable that
     * the track size's `calc()` references).
     */
    protected override render(): TemplateResult {
        const sentinelWidth = this.sentinelTrackedVariable
            ? `var(${this.sentinelTrackedVariable})`
            : '0px';
        // Sentinel is positioned absolutely with zero height to avoid
        // affecting grid layout. Width inherits the tracked variable so
        // ResizeObserver fires on resolved-value changes.
        // Sentinel uses contain:strict + grid-area:1/1 so it does not
        // influence track sizing, plus visibility:hidden + pointer-events:none
        // to be visually and interactively inert. ResizeObserver still fires
        // on width changes regardless of visibility.
        return html`
            <slot></slot>
            <span
                data-grid-sentinel="x"
                aria-hidden="true"
                style="grid-area:1/1;contain:strict;height:1px;visibility:hidden;pointer-events:none;width:${sentinelWidth};"
            ></span>
        `;
    }

    /**
     * After each render, ensure the sentinel ResizeObserver is observing
     * the current sentinel element. Re-binding is idempotent.
     */
    protected override updated(changedProperties: Map<string, unknown>): void {
        super.updated?.(changedProperties);
        this.attachSentinelResizeObserver();
    }

    /**
     * Compute the auto-axis-x size variable name (e.g. `--t3-column-section-3-size`)
     * from the current `layoutTemplate`. The variable whose resolved length
     * drives `countOfAutoAxisCellsX` is the auto-section's `sizeCssVariable`.
     *
     * Triggers a re-render if the variable name changed so the sentinel's
     * `width` gets the new `var(...)` reference.
     */
    private updateSentinelTrackedVariable(): void {
        if (!this.layoutTemplate) {
            return;
        }
        const autoSection = this.layoutTemplate.axisX.find(s => s.count === 'auto');
        if (!autoSection) {
            return;
        }
        const cssVarName = `--${this.layoutTemplate.name}-${autoSection.sizeCssVariable}`;
        if (cssVarName !== this.sentinelTrackedVariable) {
            this.sentinelTrackedVariable = cssVarName;
            this.requestUpdate();
        }
    }

    /**
     * Attach (or rebind) the ResizeObserver to the sentinel span. Safe to
     * call repeatedly; it only re-observes when the sentinel node changed.
     */
    private attachSentinelResizeObserver(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const sentinel = this.querySelector<HTMLElement>('[data-grid-sentinel="x"]');
        if (!sentinel) {
            return;
        }
        if (!this.sentinelResizeObserver) {
            this.sentinelResizeObserver = new ResizeObserver(() => {
                this.onSentinelResize();
            });
        }
        this.sentinelResizeObserver.disconnect();
        this.sentinelResizeObserver.observe(sentinel);
    }

    /**
     * Public hook (also used as the ResizeObserver callback) that recomputes
     * the grid in response to a sentinel-width change.
     *
     * Idempotent: `dispatchGridChangedEvent()` only fires `layout-size-changed`
     * when dimensions or cell counts actually changed. Safe to call on every
     * ResizeObserver tick.
     */
    public onSentinelResize(): void {
        this.dispatchGridChangedEvent();
    }

    public dispatchGridChangedEvent(): void {
        // T2.9: Single destructured getBoundingClientRect call
        const {width: containerWidth, height: containerHeight} = this.getBoundingClientRect();

        // T2.6: Zero-dimension guard (BEFORE recalculation)
        if (containerWidth === 0 || containerHeight === 0) {
            return;
        }

        // Save previous cell counts before recalculation (W1 fix)
        const prevCountX = this.countOfAutoAxisCellsX;
        const prevCountY = this.countOfAutoAxisCellsY;

        // T2.10: Call recalculateGridTemplate() to update CSS grid template
        const recalcResult = this.recalculateGridTemplate();

        // Early return if layoutTemplate not set
        if (!recalcResult) {
            return;
        }

        // Use pre-computed track counts (avoids forced reflow from getComputedStyle)
        const {columnsCount, rowsCount} = recalcResult;

        // Early return if no cells (T2.7)
        if (columnsCount === 0 || rowsCount === 0) {
            return;
        }

        // Check if dimensions changed
        const dimensionsChanged = this.dimensions.containerWidth !== containerWidth
            || this.dimensions.containerHeight !== containerHeight
            || this.dimensions.columnsCount !== columnsCount
            || this.dimensions.rowsCount !== rowsCount;

        if (!dimensionsChanged) {
            return; // No change, no event
        }

        // Update stored dimensions
        this.dimensions = {containerWidth, containerHeight, columnsCount, rowsCount};

        // T2.11: Compare new cell counts against saved previous values (W1 fix)
        const xCellCountChanged = this.countOfAutoAxisCellsX !== prevCountX;
        const yCellCountChanged = this.countOfAutoAxisCellsY !== prevCountY;

        // T2.13: Guard countOfAutoAxisCells < 1 - skip sectionsStartEndLines if either axis < 1
        const hasSufficientCells = this.countOfAutoAxisCellsX >= 1 && this.countOfAutoAxisCellsY >= 1;

        // T2.12: Conditional event dispatch with sectionsStartEndLines
        // Fire WITH sectionsStartEndLines only if cell count changed on EITHER axis AND both axes have >= 1 cell
        if ((xCellCountChanged || yCellCountChanged) && hasSufficientCells) {
            const eventDetail: LayoutSizeChangedDetail = {
                dimensions: this.dimensions,
                sectionsStartEndLines: recalcResult.sectionsStartEndLines,
                countOfAutoAxisCells: { x: this.countOfAutoAxisCellsX, y: this.countOfAutoAxisCellsY }
            };
            this.fireEvent('layout-size-changed', eventDetail);
        } else {
            // Fire WITHOUT sectionsStartEndLines (CSS surplus/remainder handles pixel difference)
            const eventDetail: LayoutSizeChangedDetail = {
                dimensions: this.dimensions
            };
            this.fireEvent('layout-size-changed', eventDetail);
        }
    }

    /**
     * Calculate columns based on columnDefinitions (CSS variable watching system).
     * Note: In timeline context, columnDefinitions are empty → this is a no-op.
     * No interference with resize flow.
     */
    private calculateColumns(): void {
        for (const [key, colDef] of Object.entries(this.columnDefinitions)) {
            if (typeof colDef === 'function') {
                // Legacy function style
                colDef(this, key, this.columnDefinitions);
            } else if (colDef && typeof colDef === 'object' && 'calculate' in colDef) {
                // New config object style with context
                const context: CalculationContext = {
                    element: this,
                    key,
                    definitions: this.columnDefinitions,
                    remainder: {
                        x: this.remainderX,
                        y: this.remainderY
                    }
                };
                colDef.calculate(context);
            }
        }
    }

    /**
     * Calculate rows based on rowDefinitions (CSS variable watching system).
     * Note: In timeline context, rowDefinitions are empty → this is a no-op.
     * No interference with resize flow.
     */
    private calculateRows(): void {
        for (const [key, rowDef] of Object.entries(this.rowDefinitions)) {
            if (typeof rowDef === 'function') {
                // Legacy function style
                rowDef(this, key, this.rowDefinitions);
            } else if (rowDef && typeof rowDef === 'object' && 'calculate' in rowDef) {
                // New config object style with context
                const context: CalculationContext = {
                    element: this,
                    key,
                    definitions: this.rowDefinitions,
                    remainder: {
                        x: this.remainderX,
                        y: this.remainderY
                    }
                };
                rowDef.calculate(context);
            }
        }
    }

    /**
     * Initialize CSS variable watching on element and ancestors
     */
    private registerCssVariableForWatchingValueChange(): void {
        // Store initial values
        this.updateTrackedVariableValues();

        // Create MutationObserver
        this.styleObserver = new MutationObserver((mutations) => {
            this.handleStyleMutations(mutations);
        });

        // Watch this element and all parent elements up to document
        let element: Element | null = this;
        while (element) {
            this.styleObserver.observe(element, {
                attributes: true,
                attributeFilter: ['style', 'class'],
                subtree: false,
                characterData: false,
                childList: false
            });
            element = element.parentElement;
        }

        // Also watch the document element for global CSS variable changes
        if (document.documentElement && document.documentElement !== this) {
            this.styleObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style', 'class'],
                subtree: false,
                characterData: false,
                childList: false
            });
        }
    }

    /**
     * Clean up CSS variable watching
     */
    private cleanupCssVariableWatching(): void {
        if (this.styleObserver) {
            this.styleObserver.disconnect();
            this.styleObserver = null;
        }
        this.trackedCssVariables.clear();
        this.cssVariablesToWatch.clear();
    }

    /**
     * Update tracked CSS variable values
     */
    private updateTrackedVariableValues(): void {
        const computedStyles = getComputedStyle(this);

        for (const varName of this.cssVariablesToWatch) {
            const value = computedStyles.getPropertyValue(varName);
            this.trackedCssVariables.set(varName, value);
        }
    }

    /**
     * Handle style mutations from MutationObserver
     */
    private handleStyleMutations(mutations: MutationRecord[]): void {
        let hasChanges = false;

        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' &&
                (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {

                hasChanges = this.checkForCssVariableChanges() || hasChanges;
            }
        });

        // If any tracked CSS variables changed, recalculate everything
        if (hasChanges) {
            this.handleTrackedCssVariableChange();
        }
    }

    /**
     * Check if any tracked CSS variables have changed
     */
    private checkForCssVariableChanges(): boolean {
        let hasChanges = false;
        const computedStyles = getComputedStyle(this);

        for (const varName of this.cssVariablesToWatch) {
            const currentValue = computedStyles.getPropertyValue(varName);
            const previousValue = this.trackedCssVariables.get(varName);

            if (currentValue !== previousValue) {
                this.trackedCssVariables.set(varName, currentValue);
                hasChanges = true;
            }
        }

        return hasChanges;
    }

    /**
     * Start polling CSS variables as fallback for DevTools changes
     */
    private startCssVariablePolling(): void {
        this.isPollingActive = true;
        this.pollCssVariables();
    }

    /**
     * Stop polling CSS variables
     */
    private stopCssVariablePolling(): void {
        this.isPollingActive = false;
        if (this.cssVariablePollingFrame !== null) {
            cancelAnimationFrame(this.cssVariablePollingFrame);
            this.cssVariablePollingFrame = null;
        }
    }

    /**
     * Poll CSS variables using requestAnimationFrame
     */
    private pollCssVariables(): void {
        if (!this.isPollingActive) {
            return;
        }

        // Check if enough time has passed since last check
        const now = performance.now();

        if (now >= this.nextCssCheckTime) {
            // Perform the check
            if (this.checkForCssVariableChanges()) {
                this.handleTrackedCssVariableChange();
            }

            // Schedule next check time (current time + range)
            this.nextCssCheckTime = now + this.CSS_CHECK_INTERVAL;
        }

        // Schedule next frame
        this.cssVariablePollingFrame = requestAnimationFrame(() => {
            this.pollCssVariables();
        });
    }

    /**
     * Public method to manually trigger CSS variable check
     */
    public checkCssVariables(): void {
        if (this.checkForCssVariableChanges()) {
            this.handleTrackedCssVariableChange();
        }
    }

    /**
     * Public API: Force a complete grid recompute.
     *
     * Use this when a CSS custom property that influences track sizing has changed
     * via a path that the internal MutationObserver/polling cannot detect immediately
     * (e.g., consumer just called `setProperty` and needs the cell count refreshed
     * synchronously before the next paint).
     *
     * Triggers `recalculateGridTemplate()` and dispatches `layout-size-changed`
     * (with `sectionsStartEndLines` + `countOfAutoAxisCells` if cell count changed).
     *
     * Idempotent: dispatches no event if neither dimensions nor cell counts changed.
     *
     * @public
     */
    public requestRecompute(): void {
        this.dispatchGridChangedEvent();
    }

    /**
     * Internal: Recompute grid layout in response to a tracked CSS variable change.
     *
     * Dispatches `layout-size-changed` (via `dispatchGridChangedEvent()`) so that
     * downstream consumers (e.g., `o2d-timeline`) refresh their viewport based on
     * the new `countOfAutoAxisCells`. Also runs the legacy `calculateColumns/Rows`
     * hooks for consumers that rely on `columnDefinitions`/`rowDefinitions`
     * (no-op in timeline context).
     *
     * Bug fix: Previously, only `calculateColumns/Rows` ran here, which is a no-op
     * when those definitions are empty (timeline use case). As a result, changes
     * to CSS variables (e.g., `--cell-width-multiplication` toggling cell width)
     * would update the visual cell size but never recompute `countOfAutoAxisCells`,
     * leaving the rendered cell count stale.
     *
     * @private
     */
    private handleTrackedCssVariableChange(): void {
        // Run legacy column/row calculation hooks (no-op when definitions empty)
        this.calculateColumns();
        this.calculateRows();

        // Recompute grid template + dispatch layout-size-changed if cell count changed.
        // dispatchGridChangedEvent() is idempotent: it only fires when dimensions or
        // cell counts actually changed, so this is safe to call on every CSS-var tick.
        this.dispatchGridChangedEvent();
    }

    public setRemainder(axisName: 'x' | 'y', value: number | string): void {
        if (typeof value === 'number') {
            value = `${value}px`;
        }

        switch (axisName) {
            case 'x':
                this.style.setProperty('--scroll_x_remainder', value);
                this.remainderX = this.fromComputedStyle('--scroll_x_remainder');
                break;
            case 'y':
                this.style.setProperty('--scroll_y_remainder', value);
                this.remainderY = this.fromComputedStyle('--scroll_y_remainder');
                break;
            default:
                throw new Error(`Invalid axis name: ${axisName}`);
        }
    }

    public getRemainder(axisName: 'x' | 'y'): number {
        switch (axisName) {
            case 'x':
                return this.remainderX;
            case 'y':
                return this.remainderY;
            default:
                throw new Error(`Invalid axis name: ${axisName}`);
        }
    }

    private fromComputedStyle(key: string): number {
        const obj = this.computedStyleMap().get(key);
        return (obj instanceof CSSStyleValue)
            ? parseFloat([...(obj as Map<string, string>).values()][0] ?? '0')
            : 0;
    }

    private buildCssTemplateAxis(templateName: string, axis: 'x' | 'y'): {
        watchVariables: string[];
        startEndLines: Map<string, StartEndLines>;
        countOfAutoAxisCells: number;
        totalTrackCount: number;
    } {
        const {axisX, axisY} = this.layoutTemplate;

        const gridSections: Section[] = axis == 'x'
            ? axisX
            : axisY;

        let {countOfAutoAxisCells, surplus, watchVariables}: CssVariablesCalculationResult = calculateGridLayoutCssVariables(
            this,
            gridSections,
            axis
        );

        // T1.5: Guard against negative countOfAutoAxisCells (container smaller than fixed size)
        countOfAutoAxisCells = Math.max(0, countOfAutoAxisCells);

        const startEndLines = new Map<string, StartEndLines>();
        const gridTemplatePartialStrings: string[] = [];
        let line = 1;
        for (const [index, gridSection] of gridSections.entries()) {
            const count = gridSection.count == 'auto'
                ? countOfAutoAxisCells
                : gridSection.count;



            const startLine = line;
            const endLine = line + count + (gridSection.type == 'fluid' ? 1 : 0);
            line = endLine;
            startEndLines.set(gridSection.sectionId, {startLine, endLine});

            switch (gridSection.type) {
                case 'fix':
                    gridTemplatePartialStrings.push(
                        `repeat(${count}, var(--${templateName}-${gridSection.sizeCssVariable}))`
                    );
                    break;
                case 'fluid':
                    gridTemplatePartialStrings.push(
                        `var(--${templateName}-${axis}-fluid-${index}-start)`,
                        `repeat(calc(${count - 1} - var(--${axis}-toggle-cell)), var(--${templateName}-${gridSection.sizeCssVariable}))`,
                        `var(--${templateName}-${axis}-fluid-${index}-end)`
                    );

                    this.setStyleProperty(`--${templateName}-${axis}-fluid-${index}-start`, `var(--${axis}-remainder-${index})`);

                    const remainder = this.getComputedRemainder(axis, index);

                    let fluidEndValue;
                    if (gridSection.count == 'auto') {
                        if (surplus - remainder >= 0) {
                            fluidEndValue = `${surplus}px - var(--${axis}-remainder-${index})`;
                        } else {
                            fluidEndValue = `var(--${templateName}-${gridSection.sizeCssVariable}) + ${surplus}px - var(--${axis}-remainder-${index})`
                        }
                    } else {
                        fluidEndValue = `var(--${templateName}-${gridSection.sizeCssVariable}) - var(--${axis}-remainder-${index})`;
                    }

                    this.setStyleProperty(`--${templateName}-${axis}-fluid-${index}-end`, `calc(${fluidEndValue})`);

                    break;
                default:
                    throw new Error(`Invalid grid axis item type: ${gridSection.type}`);
            }
        }

        this.setStyleProperty(
            axis == 'x'
                ? 'grid-template-columns'
                : 'grid-template-rows',
            gridTemplatePartialStrings.join(' ')
        );

        // Total track count derived from final grid line (avoids getComputedStyle reflow)
        const totalTrackCount = line - 1;

        return {watchVariables, startEndLines, countOfAutoAxisCells, totalTrackCount};
    }

    private getComputedRemainder(axis: "x" | "y", index: number): number {
        const value = this.computedStyleMap().get(`--${axis}-remainder-${index}`);
        if (!value) {
            // Fallback: approximate default cell size when computed style unavailable
            // (e.g., during initial render before CSS is applied)
            return 22;
        }

        return parseInt(value.toString(), 10);
    }
}